import { useCallback, useState } from 'react';

/**
 * Phase 4.11 — recent-searches history backed by ``localStorage``.
 *
 * Operator-level convenience for repeated lookups: after typing a
 * long tag like ``test_4.10_plate_cascade`` the Feed remembers the
 * last 3 unique queries and offers them as a dropdown when the
 * search input is focused and empty. No backend involvement —
 * history is per-device.
 *
 * Storage shape: a JSON-serialised ``string[]`` under the
 * ``gnss-qa:`` namespace that Phase 3.14.1 established for
 * Mini App localStorage keys (see ``FeedScreen.tsx:69-85`` for the
 * collapsed-batches-dashboard precedent). All localStorage access
 * is wrapped in ``try/catch`` so Safari private mode (where the
 * API throws on every call) degrades to in-memory state instead
 * of crashing the screen.
 *
 * Invariants enforced by ``addToHistory``:
 *   * trimmed input — whitespace-only queries are not stored.
 *   * deduplicated — adding ``"abc"`` when it's already in the
 *     list moves it to position 0 instead of duplicating.
 *   * capped at 3 entries — oldest fall off the tail.
 *
 * Hook returns the latest history array AND an ``addToHistory``
 * callback. The reactive ``history`` value triggers re-renders of
 * the dropdown so callers don't have to subscribe to storage events.
 */

const STORAGE_KEY = 'gnss-qa:search-history';
const MAX_ITEMS = 3;

function loadFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is string => typeof x === 'string')
      .map((x) => x.trim())
      .filter((x) => x.length > 0)
      .slice(0, MAX_ITEMS);
  } catch {
    return [];
  }
}

function saveToStorage(items: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* ignore quota / private-mode failures */
  }
}

export function useSearchHistory(): {
  history: string[];
  addToHistory: (query: string) => void;
} {
  const [history, setHistory] = useState<string[]>(loadFromStorage);

  const addToHistory = useCallback((query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setHistory((prev) => {
      const filtered = prev.filter((q) => q !== trimmed);
      const next = [trimmed, ...filtered].slice(0, MAX_ITEMS);
      saveToStorage(next);
      return next;
    });
  }, []);

  return { history, addToHistory };
}
